import { reactive, ref } from 'vue';
import { socket } from './socket';
import { ACCOUNT_CHANGED_EVENT, storedAccountId } from './accountStorage';
import { getSessionToken } from './session';

export type EventRequest = (name: string, payload: any) => Promise<any>;
export const eventParticipations = reactive<Record<string, any>>({});
export const eventCall = ref<any>(null);
let sessionEpoch = 0;
let awaitingSession = false;
const participationVersions = new Map<string,number>();
const queueVersions = new Map<string,number>();
const seenCalls = new Set<string>();
const closedEvents = new Set<string>();

function resetEventSession() {
  sessionEpoch++; awaitingSession = true;
  for (const id of Object.keys(eventParticipations)) delete eventParticipations[id];
  participationVersions.clear(); queueVersions.clear(); seenCalls.clear(); closedEvents.clear(); eventCall.value = null;
}
function ingestParticipation(eventId:string, data:any) {
  if (closedEvents.has(eventId)) return;
  eventParticipations[eventId] = data.participation || {
    joined:data.joined, present:data.present, joinedAt:data.joinedAt, leftAt:data.leftAt,
    myTicket:data.myTicket ?? data.queue?.myTicket ?? null,
  };
  if (data.joined === false && eventCall.value?.eventId === eventId) eventCall.value = null;
}
function ingestQueue(eventId:string, queue:any) {
  if (!queue) return;
  const revision = Number(queue.revision || 0);
  if (revision < (queueVersions.get(eventId) || 0)) return;
  queueVersions.set(eventId, revision);
  if (eventParticipations[eventId]) eventParticipations[eventId].myTicket = queue.myTicket ?? null;
  if (eventCall.value?.eventId === eventId && queue.myTicket?.status !== 'called') eventCall.value = null;
  if (!closedEvents.has(eventId) && queue.myTicket?.status === 'called') {
    const key = `${eventId}:${queue.myTicket.id}:${queue.myTicket.calledAt}`;
    if (!seenCalls.has(key)) { seenCalls.add(key); eventCall.value = {eventId,ticket:queue.myTicket}; }
  }
}
export const requestEvent: EventRequest = (name, payload) => new Promise((resolve) => {
  if (awaitingSession) return resolve({success:false,error:'Session en cours de reprise. Le registre se réouvrira après connexion.'});
  if (!socket.connected) return resolve({ success:false, error:'Connexion à la Guilde requise. Réessaie une fois reconnecté.' });
  const epoch = sessionEpoch, account = storedAccountId(), eventId = payload?.eventId;
  const before = participationVersions.get(eventId) || 0;
  try {
    socket.timeout(12_000).emit(name,payload,(error:any,response:any) => {
      if (epoch !== sessionEpoch || account !== storedAccountId()) return resolve({success:false,error:'La session a changé. Actualisez le registre.'});
      if (error) return resolve({success:false,error:'Réponse non reçue. Actualise le registre avant de réessayer.'});
      if (eventId && response?.success !== false) {
        const isRead = name === 'getEventParticipation' || name === 'getEventQueue';
        if ((response?.participation || typeof response?.joined === 'boolean') && (!isRead || before === (participationVersions.get(eventId) || 0))) {
          ingestParticipation(eventId,response);
          if (!isRead) participationVersions.set(eventId,(participationVersions.get(eventId) || 0)+1);
        }
        ingestQueue(eventId,response?.queue);
      }
      resolve(response || {success:false,error:'Réponse du registre indisponible.'});
    });
  } catch { resolve({success:false,error:'Connexion au registre interrompue. Réessayez.'}); }
});
socket.on('eventParticipationUpdated',(data:any) => {
  if (awaitingSession || !data?.eventId) return;
  participationVersions.set(data.eventId,(participationVersions.get(data.eventId) || 0)+1);
  ingestParticipation(data.eventId,data);
});
socket.on('eventQueueUpdated',(data:any) => { if (!awaitingSession && data?.eventId) ingestQueue(data.eventId,data.queue); });
socket.on('eventQueueCalled',(data:any) => {
  if (awaitingSession || !data?.eventId || !data?.ticket || closedEvents.has(data.eventId)) return;
  const ticket = eventParticipations[data.eventId]?.myTicket;
  if (ticket?.id === data.ticket.id && Number(ticket.updatedAt || 0) > Number(data.ticket.calledAt || 0) && ticket.status !== 'called') return;
  seenCalls.add(`${data.eventId}:${data.ticket.id}:${data.ticket.calledAt}`);
  eventCall.value = {...data,eventTitle:data.eventTitle || data.title};
});
function closeLocalEvent(payload:any) {
  const eventId = typeof payload === 'string' ? payload : payload?.id || payload?.eventId;
  if (!eventId) return;
  closedEvents.add(eventId);
  participationVersions.set(eventId,(participationVersions.get(eventId) || 0)+1);
  delete eventParticipations[eventId];
  if (eventCall.value?.eventId === eventId) eventCall.value = null;
}
socket.on('eventUpdated',(event:any) => { if (event?.closedAt) closeLocalEvent(event); });
socket.on('eventCancelled',closeLocalEvent);
socket.on('disconnect',resetEventSession);
socket.on('forceDisconnect',resetEventSession);
window.addEventListener(ACCOUNT_CHANGED_EVENT,resetEventSession);
// La connexion transport précède parfois bindSession. Restaurer après ce sceau
// recharge aussi les tickets des événements dont le registre n'est pas ouvert.
window.addEventListener('guild-session-bound',() => { awaitingSession = false; void restoreEventParticipations(); });
async function restoreEventParticipations() {
  if (!getSessionToken()) return;
  const epoch = sessionEpoch;
  const result = await requestEvent('getActiveEvents',{});
  if (epoch !== sessionEpoch || !Array.isArray(result?.events)) return;
  const events = result.events.filter((event:any) => event.lifecycleVersion === 2 && !event.closedAt);
  for (let offset = 0; offset < events.length && epoch === sessionEpoch; offset += 10) {
    await Promise.all(events.slice(offset,offset+10).map((event:any) => requestEvent('getEventParticipation',{eventId:event.id})));
  }
}

export function eventCommandKey() { return crypto.randomUUID(); }
